import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface AfipConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: AfipConfig) => void;
}

interface AfipConfig {
  cuit: string;
  certificateFile: File | null;
  privateKeyFile: File | null;
}

const AfipConfigModal: React.FC<AfipConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [config, setConfig] = useState<AfipConfig>({
    cuit: '',
    certificateFile: null,
    privateKeyFile: null,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(config);
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg">
          <Dialog.Title className="text-xl font-semibold mb-4">
            Configuración AFIP
          </Dialog.Title>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label="CUIT"
                id="cuit"
                name="cuit"
                value={config.cuit}
                onChange={(e) => setConfig({ ...config, cuit: e.target.value })}
                required
                placeholder="20-12345678-9"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Certificado Digital
                </label>
                <input
                  type="file"
                  accept=".crt"
                  onChange={(e) => setConfig({ 
                    ...config, 
                    certificateFile: e.target.files?.[0] || null 
                  })}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-indigo-50 file:text-indigo-700
                    hover:file:bg-indigo-100"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clave Privada
                </label>
                <input
                  type="file"
                  accept=".key"
                  onChange={(e) => setConfig({ 
                    ...config, 
                    privateKeyFile: e.target.files?.[0] || null 
                  })}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-indigo-50 file:text-indigo-700
                    hover:file:bg-indigo-100"
                  required
                />
              </div>
            </div>
            
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-4">
                Para obtener el certificado digital y la clave privada:
                <ol className="list-decimal ml-4 mt-2">
                  <li>Ingrese al sitio de AFIP con clave fiscal</li>
                  <li>Acceda a "Administrador de Relaciones de Clave Fiscal"</li>
                  <li>Seleccione "Nueva Relación"</li>
                  <li>Busque y agregue "Web Services"</li>
                  <li>Genere el certificado y la clave privada</li>
                </ol>
              </p>
            </div>
            
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!config.cuit || !config.certificateFile || !config.privateKeyFile}
              >
                Guardar
              </Button>
            </div>
          </form>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AfipConfigModal;